/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import cx from 'classnames';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Skeleton, Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { org_coursera_diagnostics_DiagnosticProgressState as LevelSetProgressState } from 'bundles/program-skills-objectives/components/ProgramSkillsObjectivesPage/__generated__/globalTypes';

import _t from 'i18n!nls/program-skills-objectives';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<'a'>>(
  // TODO(ppaskaris): Ask how to type this.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Button
);

type Props = {
  className?: string;
  skillName: string;
  skillId: string;
  programSlug: string;
  objectiveSlug: string;
  numQuestions?: number;
  durationMinutes?: number;
  progressState: LevelSetProgressState;
  attemptsLeft: number;
  /**
   * Gets appended to the levelset url with the key 'source' for analytics purposes
   */
  linkSource?: string;
};

function getLabel(inProgress: boolean, progressState: LevelSetProgressState) {
  if (inProgress) {
    return _t('Finish assessment');
  }
  if (progressState === 'COMPLETED') {
    return _t('Retake assessment');
  }
  return _t('Check my level');
}

export const levelSetBackgroundURL = `"data:image/svg+xml,%3Csvg width='52' height='84' viewBox='0 0 52 84' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M0 -35.5881C0 -37.0109 0.755788 -38.3266 1.98484 -39.0434L66.8994 -76.9021C68.1452 -77.6286 69.6857 -77.6283 70.9312 -76.9013L135.783 -39.0437C137.011 -38.3267 137.766 -37.0114 137.766 -35.5892V40.2016C137.766 41.6238 137.011 42.9391 135.783 43.6561L70.9316 81.5134C69.6859 82.2406 68.1451 82.2408 66.8992 81.5139L1.98423 43.6396C0.75552 42.9227 0 41.6072 0 40.1846V-35.5881ZM127.263 -29.4491C127.263 -30.8718 126.508 -32.1874 125.279 -32.9042L70.9306 -64.6072C69.6852 -65.3337 68.1451 -65.3337 66.8997 -64.6072L12.5516 -32.9042C11.3227 -32.1874 10.5671 -30.8718 10.5671 -29.4491V34.0456C10.5671 35.468 11.3224 36.7834 12.5509 37.5003L66.8994 69.2189C68.145 69.9458 69.6854 69.946 70.9311 69.2193L125.279 37.5165C126.508 36.7997 127.263 35.4841 127.263 34.0614V-29.4491Z' fill='%23E1EAF5'/%3E%3Cpath d='M0 42.4819L10.454 36.2771L69.0128 70.4281L68.9155 82.674L0 42.4819Z' fill='black' fill-opacity='0.08'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M47.875 4.34644C49.8669 4.34644 51.5615 5.62144 52.1896 7.40106L72.275 7.40115C73.1172 7.40115 73.7999 8.08497 73.7999 8.92851C73.7999 9.77205 73.1172 10.4559 72.275 10.4559L52.1892 10.457C51.5609 12.2361 49.8665 13.5106 47.875 13.5106C45.8834 13.5106 44.189 12.2361 43.5607 10.457L38.725 10.4559C37.8827 10.4559 37.2 9.77205 37.2 8.92851C37.2 8.08497 37.8827 7.40115 38.725 7.40115L43.5603 7.40106C44.1884 5.62144 45.883 4.34644 47.875 4.34644ZM47.875 6.38291C46.4712 6.38291 45.3333 7.52262 45.3333 8.92851C45.3333 10.3344 46.4712 11.4741 47.875 11.4741C49.2787 11.4741 50.4166 10.3344 50.4166 8.92851C50.4166 7.52262 49.2787 6.38291 47.875 6.38291Z' fill='%23E1EAF5'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M62.1081 18.6018C60.1162 18.6018 58.4216 19.8768 57.7935 21.6564L38.7248 21.6565C37.8826 21.6565 37.1998 22.3403 37.1998 23.1839C37.1998 24.0274 37.8826 24.7112 38.7248 24.7112L57.7939 24.7123C58.4222 26.4914 60.1166 27.766 62.1081 27.766C64.0997 27.766 65.7941 26.4914 66.4224 24.7123L72.2748 24.7112C73.117 24.7112 73.7998 24.0274 73.7998 23.1839C73.7998 22.3403 73.117 21.6565 72.2748 21.6565L66.4228 21.6564C65.7947 19.8768 64.1001 18.6018 62.1081 18.6018ZM62.1081 20.6383C63.5119 20.6383 64.6498 21.778 64.6498 23.1839C64.6498 24.5898 63.5119 25.7295 62.1081 25.7295C60.7044 25.7295 59.5665 24.5898 59.5665 23.1839C59.5665 21.778 60.7044 20.6383 62.1081 20.6383Z' fill='%23E1EAF5'/%3E%3C/svg%3E%0A"`;

const useStyles = () => {
  return {
    container: css`
      box-shadow: 0 1px 4px rgb(49 49 49 / 24%);
      border-radius: 2px;
      background-color: var(--cds-color-neutral-background-primary);
    `,
    header: css`
      height: 84px;
      display: flex;
      align-items: center;
      background: var(--cds-color-grey-25) url(${levelSetBackgroundURL}) top right no-repeat;
    `,
    headerChange: css`
      height: 84px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      background: var(--cds-color-grey-25) url(${levelSetBackgroundURL}) top right no-repeat;

      // trick to apply rtl styles
      // cannot use :dir(rtl) because elements inside the react-slick is still ltr
      html[dir='rtl'] & {
        align-items: flex-end;
      }
    `,
    headerTitle: css`
      margin-left: var(--cds-spacing-200);
      margin-top: var(--cds-spacing-200);
      text-transform: uppercase;
    `,
    headerTextChange: css`
      margin-left: var(--cds-spacing-200);
      margin-top: var(--cds-spacing-100);
    `,
    headerText: css`
      margin-left: var(--cds-spacing-200);
    `,
    cardBody: css`
      padding: var(--cds-spacing-200);
      text-align: left;
    `,
    cardBodyHeading: css`
      margin-bottom: var(--cds-spacing-100);
      min-height: 40px;
    `,
    pipe: css`
      margin: 0 var(--cds-spacing-100);
    `,
    pipeDot: css`
      margin: 0 var(--cds-spacing-50);
    `,
    button: css`
      display: block;
      margin-top: var(--cds-spacing-200);
      text-align: center;
    `,
    completeBoth: css`
      display: flex;
      align-items: center;
      height: 32px;
      margin-top: var(--cds-spacing-800);
    `,
    completeText: css`
      display: flex;
      align-items: center;
      margin-bottom: var(--cds-spacing-200);
    `,
    checkSign: css`
      margin-right: var(--cds-spacing-50);
    `,
  };
};

const LevelSetCard = ({
  className,
  skillName,
  skillId,
  programSlug,
  objectiveSlug,
  durationMinutes,
  progressState,
  attemptsLeft,
  linkSource,
}: Props) => {
  const styles = useStyles();
  const inProgress = progressState === LevelSetProgressState.IN_PROGRESS;
  const sourceParam = linkSource ? `?source=${linkSource}` : '';

  const label = getLabel(inProgress, progressState);

  return (
    <TrackedDiv
      className={cx('rc-LevelSetCard', className)}
      css={styles.container}
      trackingName="level_set_card"
      withVisibilityTracking={true}
      data={{ skillId }}
    >
      <div css={styles.headerChange}>
        <Typography2 variant="subtitleMedium" color="supportText" component="span" css={styles.headerTitle}>
          {_t('Levelset')}
        </Typography2>
        <Typography2 variant="subtitleLarge" component="span" css={styles.headerTextChange}>
          {skillName}
        </Typography2>
      </div>
      {attemptsLeft === 0 ? (
        <div className="card-body" css={styles.cardBody}>
          <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.cardBodyHeading}>
            {_t('You have completed all assessments for this LevelSet')}
          </Typography2>
          <div css={styles.completeBoth}>
            <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.completeText}>
              <CheckIcon size="large" css={styles.checkSign} /> {_t('Completed 2 of 2 attempts')}
            </Typography2>
          </div>
        </div>
      ) : (
        <div className="card-body" css={styles.cardBody}>
          <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.cardBodyHeading}>
            {progressState === 'COMPLETED'
              ? _t('Think you can do better? You have 1 more chance to take this assessment')
              : _t('Demonstrate your skills to skip ahead.')}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" color="supportText">
            {durationMinutes && (
              <>
                <FormattedMessage message={_t('{duration} mins')} duration={durationMinutes} />

                <span aria-hidden css={styles.pipeDot}>
                  •
                </span>
              </>
            )}
            <FormattedMessage message={_t('15-20 questions')} />
          </Typography2>
          <TrackedButton
            {...Button.defaultProps}
            fullWidth
            trackingName="start_diagnostics"
            variant={inProgress ? 'primary' : 'secondary'}
            aria-label={`${label}: ${skillName}`}
            component="a"
            href={`/levelsets/${skillId}${sourceParam}&programSlug=${programSlug}&objectiveSlug=${objectiveSlug}&skillId=${skillId}`}
            css={styles.button}
            trackingData={{ skillId }}
          >
            {label}
          </TrackedButton>
        </div>
      )}
    </TrackedDiv>
  );
};

type PropsForPlaceholder = Pick<Props, 'className'>;

const LevelSetCardPlaceholder = ({ className }: PropsForPlaceholder) => {
  const styles = useStyles();
  return (
    <div className={cx('rc-LevelSetCard', className)} css={styles.container} aria-label={_t('Loading...')} aria-busy>
      <div css={styles.headerChange}>
        <Typography2 variant="subtitleMedium" color="supportText" component="span" css={styles.headerTitle}>
          {_t('Levelset')}
        </Typography2>
        <Typography2 variant="subtitleLarge" component="span" css={styles.headerTextChange} aria-hidden>
          <Skeleton width={96} />
        </Typography2>
      </div>
      <div className="card-body" css={styles.cardBody}>
        <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.cardBodyHeading} aria-hidden>
          {_t('Demonstrate your skills to skip ahead.')}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" aria-hidden>
          <Skeleton />
        </Typography2>
        <Button fullWidth variant="secondary" css={styles.button} disabled aria-hidden>
          <Skeleton />
        </Button>
      </div>
    </div>
  );
};

LevelSetCard.Placeholder = LevelSetCardPlaceholder;

export default LevelSetCard;
